import type { Hero, HeroType, DamageRange } from "@/types/hero.types"
import type { Enemy } from "@/types/enemy.types"
import type { Position } from "@/types/game.types"
import type { HeroInventory } from "@/types/item.types"
import { heroTypes, heroLevelConfig, getExperienceForLevel } from "@/data/hero-types"
import { generateId } from "@/utils/id-generator"
import { distanceSquared } from "@/utils/collision"
import { moveTowards, calculateAngle } from "@/utils/pathfinding"
import { getEmptyShrineBuffs } from "./shrine-system"
import { getEquipmentAttackSpeedBonus, getEquipmentSpeedBonus } from "./item-system"
import { gameNow } from "@/utils/game-clock"

export const createHero = (type: HeroType, position: Position): Hero => {
  const config = heroTypes[type]
  if (!config) {
    throw new Error(`Hero type ${type} not found`)
  }

  return {
    id: generateId("hero"),
    type,
    name: config.displayName, // Use display name
    position: { ...position },
    targetPosition: null,
    health: config.baseStats.health,
    maxHealth: config.baseStats.health,
    mana: config.baseStats.mana, // Added mana
    maxMana: config.baseStats.mana, // Added maxMana
    damage: { ...config.baseStats.damage }, // Changed to damage range
    armor: config.baseStats.armor, // Added armor
    damageType: config.damageType, // Added damage type
    armorType: config.armorType, // Added armor type
    speed: config.baseStats.speed,
    attackRange: config.baseStats.attackRange,
    attackSpeed: config.baseStats.attackSpeed,
    attackCooldown: 0,
    isMoving: false,
    isAttacking: false,
    target: null,
    color: config.color,
    rotation: 0,
    upgrades: {
      health: 0,
      damage: 0,
      speed: 0,
      attackSpeed: 0,
    },
    abilities: config.abilities.map((ability) => ({ ...ability })),
    skills: config.skills.map((skill) => ({ ...skill, currentCooldown: 0 })),
    level: 1,
    experience: 0,
    experienceToNextLevel: getExperienceForLevel(1),
    damageMultiplier: 1,
    healthMultiplier: 1,
    skillPoints: 1, // Start with 1 skill point at level 1
    // Dash state initialized
    isDashing: false,
    dashDirection: null,
    dashProgress: 0,
    dashMaxDistance: 0,
    dashDamage: 0,
    dashSpeed: 0,
    dashSlowValue: 0,
    dashSlowDuration: 0,
    dashHitEnemies: [],
    // Active buffs initialized
    activeBuffs: [],
    // Animation state initialized
    animationState: "idle",
    animationStartTime: gameNow(),
    hurtTimer: 0,
    lastMovementTime: gameNow(),
    isPlayingRelaxedIdle: false,
    activeSkillAnimation: null,
    randomAttackVariant: 0,
    lastIdleAnimationTime: gameNow(),
    selectedIdleVariant: 0,
    // Forcefield (Rift Blade only) - 20% of max health
    forcefield: type === "paladin" ? Math.floor(config.baseStats.health * 0.2) : 0,
    maxForcefield: type === "paladin" ? Math.floor(config.baseStats.health * 0.2) : 0,
    // Slow debuff (starts with none)
    slowMultiplier: 1,
    slowEndFrame: 0,
    // Shrine buffs (initialized to empty)
    shrineBuffs: getEmptyShrineBuffs(),
  }
}

export const calculateHeroDamage = (hero: Hero): number => {
  const { min, max } = hero.damage
  return Math.floor(min + Math.random() * (max - min + 1))
}

export const getScaledDamageRange = (hero: Hero): DamageRange => {
  return {
    min: Math.floor(hero.damage.min * hero.damageMultiplier),
    max: Math.floor(hero.damage.max * hero.damageMultiplier),
  }
}

export const setHeroDestination = (hero: Hero, destination: Position): Hero => {
  return {
    ...hero,
    targetPosition: { ...destination },
    isMoving: true,
  }
}

export const findNearestEnemy = (hero: Hero, enemies: Enemy[]): Enemy | null => {
  let nearestEnemy: Enemy | null = null
  let minDistanceSquared = Number.POSITIVE_INFINITY
  const attackRangeSquared = hero.attackRange * hero.attackRange

  for (let i = 0; i < enemies.length; i++) {
    const enemy = enemies[i]
    const distSq = distanceSquared(hero.position, enemy.position)
    if (distSq <= attackRangeSquared && distSq < minDistanceSquared) {
      minDistanceSquared = distSq
      nearestEnemy = enemy
    }
  }

  return nearestEnemy
}

export const updateHero = (hero: Hero, enemies: Enemy[], delta: number, inventory?: HeroInventory): Hero => {
  const updated = { ...hero }
  const now = gameNow()

  if (updated.attackCooldown > 0) {
    updated.attackCooldown -= delta
  }

  // Decrease hurt timer
  if (updated.hurtTimer > 0) {
    updated.hurtTimer -= delta
    if (updated.hurtTimer <= 0 && updated.animationState === "hurt") {
      // Hurt animation finished, return to appropriate state
      updated.animationState = "idle"
      updated.animationStartTime = now
    }
  }

  // Don't update movement/attack if dead
  if (updated.animationState === "death") {
    return updated
  }

  // Check if hero has an active move order first
  if (updated.targetPosition) {
    const distSq = distanceSquared(updated.position, updated.targetPosition)
    if (distSq < 25) {
      // 5^2 = 25 - reached destination
      updated.targetPosition = null
      updated.isMoving = false
    } else {
      // Move towards destination (apply slow multiplier, shrine speed bonus, and equipment speed bonus)
      const equipmentSpeedBonus = inventory ? getEquipmentSpeedBonus(inventory) : 0
      const effectiveSpeed = updated.speed * updated.slowMultiplier * (1 + updated.shrineBuffs.movementSpeedBonus + equipmentSpeedBonus)
      updated.position = moveTowards(updated.position, updated.targetPosition, effectiveSpeed * delta)
      updated.isMoving = true
      updated.lastMovementTime = now
      const angle = calculateAngle(updated.position, updated.targetPosition)
      updated.rotation = angle

      // Interrupt relaxed idle if moving. A move order is time-critical, so the
      // hero stands straight into the walk cycle rather than playing a get-up
      // animation the player did not ask to wait for.
      if (updated.isPlayingRelaxedIdle) {
        updated.isPlayingRelaxedIdle = false
      }

      // Can still attack while moving if enemy is in range
      const nearestEnemy = findNearestEnemy(updated, enemies)
      if (nearestEnemy) {
        updated.isAttacking = true
        updated.target = nearestEnemy.id
        // Face the enemy while moving (optional: rotate towards enemy)
        updated.rotation = calculateAngle(updated.position, nearestEnemy.position)
      } else {
        updated.isAttacking = false
        updated.target = null
      }

      // Set walk animation if not in hurt/death/skill state
      if (updated.animationState !== "hurt" && updated.animationState !== "skill") {
        if (updated.animationState !== "walk") {
          updated.animationState = "walk"
          updated.animationStartTime = now
        }
      }

      return updated
    }
  }

  // No move order - check for enemies to attack
  const nearestEnemy = findNearestEnemy(updated, enemies)

  if (nearestEnemy) {
    updated.isAttacking = true
    updated.target = nearestEnemy.id
    updated.isMoving = false
    const angle = calculateAngle(updated.position, nearestEnemy.position)
    updated.rotation = angle

    // Fighting counts as activity, so the relaxed idle timer restarts once combat ends
    updated.lastMovementTime = now

    // Interrupt relaxed idle if a fight starts
    if (updated.isPlayingRelaxedIdle) {
      updated.isPlayingRelaxedIdle = false
    }

    // Set attack animation if not in hurt/skill state (death returns early above)
    if (updated.animationState !== "hurt" && updated.animationState !== "skill") {
      if (updated.animationState !== "attack") {
        updated.animationState = "attack"
        updated.animationStartTime = now
        // Pick random attack variant
        const spriteConfig = getHeroSpriteAttackCount(updated.type)
        updated.randomAttackVariant = Math.floor(Math.random() * spriteConfig)
      }
    }
    return updated
  } else {
    updated.isAttacking = false
    updated.target = null
  }

  updated.isMoving = false

  // Set idle animation if not in hurt/skill state (death returns early above)
  if (updated.animationState !== "hurt" && updated.animationState !== "skill" &&
      updated.animationState !== "relaxed-idle") {
    if (updated.animationState !== "idle") {
      updated.animationState = "idle"
      updated.animationStartTime = now
    }
  }

  return updated
}

/**
 * Get number of attack variants for a hero type
 */
function getHeroSpriteAttackCount(heroType: string): number {
  switch (heroType) {
    case "paladin":
      return 3
    case "siegesmith":
      return 2
    default:
      return 1
  }
}

export const canHeroAttack = (hero: Hero): boolean => {
  return hero.isAttacking && hero.attackCooldown <= 0 && hero.target !== null
}

/**
 * Start the hero's attack cooldown.
 *
 * `attackSpeed` is the cooldown length in frames, so an attack-speed bonus
 * divides it: +30% means the next swing is ready in 1/1.3 of the time. The
 * getter floors the bonus above -1, so the divisor is always positive.
 */
export const performHeroAttack = (hero: Hero, inventory?: HeroInventory): Hero => {
  const attackSpeedBonus = inventory ? getEquipmentAttackSpeedBonus(inventory) : 0
  return {
    ...hero,
    attackCooldown: hero.attackSpeed / (1 + attackSpeedBonus),
  }
}

export const damageHero = (hero: Hero, damage: number, frameCount?: number): Hero => {
  let remainingDamage = damage
  let newForcefield = hero.forcefield
  let lastForcefieldDamageFrame = hero.lastForcefieldDamageFrame

  // Forcefield absorbs damage first (Rift Blade only)
  if (hero.forcefield > 0) {
    // Track when forcefield took damage (for 3s recharge delay)
    lastForcefieldDamageFrame = frameCount ?? hero.lastForcefieldDamageFrame

    if (hero.forcefield >= remainingDamage) {
      newForcefield = hero.forcefield - remainingDamage
      remainingDamage = 0
    } else {
      remainingDamage -= hero.forcefield
      newForcefield = 0
    }
  }

  const newHealth = Math.max(0, hero.health - remainingDamage)
  const isDead = newHealth <= 0

  return {
    ...hero,
    forcefield: newForcefield,
    lastForcefieldDamageFrame,
    health: newHealth,
    lastDamageFrame: frameCount ?? hero.lastDamageFrame,
    // Trigger hurt or death animation
    animationState: isDead ? "death" : "hurt",
    animationStartTime: gameNow(),
    hurtTimer: isDead ? 9999 : 30, // Death holds indefinitely, hurt lasts ~0.5s
    // Interrupt relaxed idle if active
    isPlayingRelaxedIdle: false,
  }
}

export const healHero = (hero: Hero): Hero => {
  return {
    ...hero,
    health: hero.maxHealth,
  }
}

export const isHeroDead = (hero: Hero): boolean => {
  return hero.health <= 0
}

export const getHeroUpgradeCost = (
  heroType: HeroType,
  stat: "health" | "damage" | "speed" | "attackSpeed",
  currentLevel: number,
): number | null => {
  const config = heroTypes[heroType]
  const upgrade = config.upgrades.find((u) => u.stat === stat && u.level === currentLevel + 1)
  return upgrade ? upgrade.cost : null
}

export const upgradeHeroStat = (
  hero: Hero,
  stat: "health" | "damage" | "speed" | "attackSpeed",
): { hero: Hero; cost: number } | null => {
  const config = heroTypes[hero.type]
  const currentLevel = hero.upgrades[stat]
  const upgrade = config.upgrades.find((u) => u.stat === stat && u.level === currentLevel + 1)

  if (!upgrade) {
    return null
  }

  const updated = { ...hero }
  updated.upgrades = { ...updated.upgrades, [stat]: currentLevel + 1 }

  switch (stat) {
    case "health":
      updated.maxHealth = upgrade.value
      updated.health = Math.min(updated.health, updated.maxHealth)
      break
    case "damage": {
      const damageRatio = upgrade.value / config.baseStats.damage.max
      updated.damage = {
        min: Math.floor(config.baseStats.damage.min * damageRatio),
        max: upgrade.value,
      }
      break
      }
    case "speed":
      updated.speed = upgrade.value
      break
    case "attackSpeed":
      updated.attackSpeed = upgrade.value
      break
  }

  return {
    hero: updated,
    cost: upgrade.cost,
  }
}

export const grantExperience = (hero: Hero, experience: number): { hero: Hero; leveledUp: boolean } => {
  if (hero.level >= heroLevelConfig.maxLevel) {
    return { hero, leveledUp: false }
  }

  // Apply shrine XP gain bonus
  const xpMultiplier = 1 + hero.shrineBuffs.xpGainBonus
  const adjustedExperience = Math.floor(experience * xpMultiplier)

  let updated = { ...hero }
  updated.experience += adjustedExperience
  let leveledUp = false

  while (updated.experience >= updated.experienceToNextLevel && updated.level < heroLevelConfig.maxLevel) {
    updated = levelUp(updated)
    leveledUp = true
  }

  return { hero: updated, leveledUp }
}

export const levelUp = (hero: Hero): Hero => {
  const newLevel = hero.level + 1
  const config = heroTypes[hero.type]

  const newMultiplier = Math.pow(1 + heroLevelConfig.statMultiplierPerLevel, newLevel - 1)

  const baseMaxHealth = config.baseStats.health
  const newMaxHealth = Math.floor(baseMaxHealth * newMultiplier)

  const baseDamage = config.baseStats.damage
  const newDamage: DamageRange = {
    min: Math.floor(baseDamage.min * newMultiplier),
    max: Math.floor(baseDamage.max * newMultiplier),
  }

  // Recalculate forcefield for Rift Blade (20% of new max health)
  const newMaxForcefield = hero.type === "paladin" ? Math.floor(newMaxHealth * 0.2) : 0

  return {
    ...hero,
    level: newLevel,
    experience: hero.experience - hero.experienceToNextLevel,
    experienceToNextLevel: getExperienceForLevel(newLevel),
    damageMultiplier: newMultiplier,
    healthMultiplier: newMultiplier,
    maxHealth: newMaxHealth,
    health: newMaxHealth,
    damage: newDamage,
    skillPoints: hero.skillPoints + 1, // Grant 1 skill point per level
    maxForcefield: newMaxForcefield,
    forcefield: newMaxForcefield, // Restore forcefield on level up
  }
}

export const getHeroExperienceProgress = (hero: Hero): number => {
  if (hero.level >= heroLevelConfig.maxLevel) return 1
  return hero.experience / hero.experienceToNextLevel
}

/**
 * Easing function for smooth dash animation (ease-in-out cubic)
 * Returns a speed multiplier based on progress (0-1)
 */
const getDashSpeedMultiplier = (progress: number): number => {
  // Use ease-in-out cubic for smooth acceleration and deceleration
  // Peaks at 2x in the middle, starts and ends slower
  if (progress < 0.5) {
    // Ease in: accelerate from 0.3 to 2.0
    return 0.3 + 3.4 * (progress * 2) * (progress * 2)
  } else {
    // Ease out: decelerate from 2.0 back to 0.3
    const t = (progress - 0.5) * 2
    return 2.0 - 3.4 * t * t + 0.3
  }
}

/**
 * Check if a point is within a certain distance of a line segment.
 * Uses line-to-circle collision for sweep detection.
 */
const pointToLineSegmentDistanceSq = (
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number => {
  const dx = x2 - x1
  const dy = y2 - y1
  const lengthSq = dx * dx + dy * dy

  if (lengthSq === 0) {
    // Segment is a point
    const ddx = px - x1
    const ddy = py - y1
    return ddx * ddx + ddy * ddy
  }

  // Project point onto line, clamping to segment
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSq))
  const projX = x1 + t * dx
  const projY = y1 + t * dy

  const distX = px - projX
  const distY = py - projY
  return distX * distX + distY * distY
}

/**
 * Update hero dash state. Returns updated hero and list of enemy IDs that were hit.
 */
export const updateHeroDash = (
  hero: Hero,
  enemies: Enemy[],
  delta: number,
): { hero: Hero; hitEnemyIds: string[] } => {
  if (!hero.isDashing || !hero.dashDirection) {
    return { hero, hitEnemyIds: [] }
  }

  // Apply easing based on current progress
  const progressPercent = hero.dashMaxDistance > 0
    ? hero.dashProgress / hero.dashMaxDistance
    : 0
  const speedMultiplier = getDashSpeedMultiplier(progressPercent)

  // Reduced dash speed (0.6x of original for smoother, more visible dash)
  const moveDistance = hero.dashSpeed * speedMultiplier * delta * 0.6
  const newProgress = hero.dashProgress + moveDistance

  // Store start position for sweep collision
  const startPosition = hero.position

  // Calculate new position
  const newPosition: Position = {
    x: hero.position.x + hero.dashDirection.x * moveDistance,
    y: hero.position.y + hero.dashDirection.y * moveDistance,
  }

  // Check for enemy collisions using line segment sweep
  const hitEnemyIds: string[] = []
  const hitRadius = 80 // Collision radius during dash (wide sweep for Vanguard Rush)
  const hitRadiusSq = hitRadius * hitRadius

  for (const enemy of enemies) {
    // Skip already hit enemies or dead enemies
    if (hero.dashHitEnemies.includes(enemy.id)) continue
    if (enemy.health <= 0) continue

    // Use sweep collision - check distance from enemy to the line segment of hero's movement
    const distSq = pointToLineSegmentDistanceSq(
      enemy.position.x,
      enemy.position.y,
      startPosition.x,
      startPosition.y,
      newPosition.x,
      newPosition.y,
    )

    if (distSq <= hitRadiusSq) {
      hitEnemyIds.push(enemy.id)
    }
  }

  // Check if dash is complete
  const isDashComplete = newProgress >= hero.dashMaxDistance

  return {
    hero: {
      ...hero,
      position: newPosition,
      dashProgress: newProgress,
      dashHitEnemies: [...hero.dashHitEnemies, ...hitEnemyIds],
      isDashing: !isDashComplete,
      // Reset dash state when complete
      ...(isDashComplete && {
        dashDirection: null,
        dashProgress: 0,
        dashMaxDistance: 0,
        dashDamage: 0,
        dashSpeed: 0,
        dashSlowValue: 0,
        dashSlowDuration: 0,
        dashHitEnemies: [],
      }),
    },
    hitEnemyIds,
  }
}

/**
 * Get effective max health with shrine health bonus applied
 */
export const getEffectiveHeroMaxHealth = (hero: Hero): number => {
  return Math.floor(hero.maxHealth * (1 + hero.shrineBuffs.healthBonus))
}

/**
 * Get effective damage range with shrine damage bonus applied
 */
export const getEffectiveHeroDamage = (hero: Hero): DamageRange => {
  const mult = 1 + hero.shrineBuffs.damageBonus
  return {
    min: Math.floor(hero.damage.min * mult),
    max: Math.floor(hero.damage.max * mult),
  }
}
